/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */


/**
 * The three step-back flows, as the wording the contract gives each one. They are the transitions a
 * destination status cannot identify on its own — all three land somewhere before review — so a
 * recorded action is the only thing that names them. They are also the only ones a note belongs to.
 */
const STEP_BACK_VERBS = {
    withdrawn: 'withdrawn',
    returned: 'returned with changes requested',
    reopened: 'reopened for review',
};

/**
 * The two transitions a destination status names on its own. Null for a destination this does not
 * recognise, which is the caller's cue to fall back to neutral wording rather than assert a
 * transition that may not have happened.
 */
const destinationVerb = (newStatusCode) => {
    switch (newStatusCode) {
        case 'in-review':
            return 'submitted for review';
        case 'review-complete':
            return 'accepted';
        default:
            return null;
    }
};

/**
 * How a survey submission's stored transition reads: the verb for the sentence, and whether a
 * reviewer note belongs to it.
 *
 * The submission's own recorded transition names all five flows. `unsubmitAction` is the older shape
 * and carries only the three step-back ones, so it is read second: `??` is what reads both, since the
 * newer key is present-and-null on rows written before a transition was recorded — as common here as
 * the key being absent — and both have to fall through. Rows carrying only the older key cannot tell
 * a submit from a reopen (both land on in-review) and are read as the common case.
 *
 * A note belongs to a step back — it says what needs changing — so a submit and an accept render the
 * transition alone. An unrecognised destination is a legacy step back and keeps its note.
 *
 * The daily-summary email derives the same reading in its own PHP half, ActivityLineRegistry's
 * SubmissionVerb; the wording is fixed by documentation/Specifications/Activity Description Copy
 * Contract.md and tests/preflight/check-activity-grammar.js runs one set of sentences through both.
 * That gate evaluates this file without a build, so keep it dependency-free.
 */
export const submissionTransition = (data) => {
    const action = data?.newData?.transitionAction ?? data?.unsubmitAction;
    const stepBackVerb = STEP_BACK_VERBS[action] ?? null;
    const verb = stepBackVerb ?? destinationVerb(data?.newData?.SurveySubmissionStatus?.code);

    return {verb, keepsNote: !verb || stepBackVerb !== null};
};
